import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';

const FontSizeContext = createContext();

const fontPresets = {
  xsmall: {
    '--font-size-xs': '8px',
    '--font-size-sm': '10px',
    '--font-size-md': '12px',
    '--font-size-lg': '14px',
    '--font-size-xl': '16px',
    '--font-size-xxl': '18px',

    // Icon
    '--icon-size-xs': '4px',
    '--icon-size-xsm': '6px',
    '--icon-size-sm': '10px',
    '--icon-size-md': '12px',
    '--icon-size-lg': '14px',
    '--icon-size-xl': '16px',
    '--icon-size-xxl': '18px',
    '--icon-size-xxxl': '20px',
  },
  small: {
    '--font-size-xs': '9px',
    '--font-size-sm': '11px',
    '--font-size-md': '13px',
    '--font-size-lg': '15px',
    '--font-size-xl': '17px',
    '--font-size-xxl': '19px',

    // Icon
    '--icon-size-xs': '5px',
    '--icon-size-xsm': '7px',
    '--icon-size-sm': '11px',
    '--icon-size-md': '13px',
    '--icon-size-lg': '15px',
    '--icon-size-xl': '17px',
    '--icon-size-xxl': '19px',
    '--icon-size-xxxl': '22px',
  },
  default: {
    '--font-size-xs': '10px',
    '--font-size-sm': '12px',
    '--font-size-md': '14px',
    '--font-size-lg': '16px',
    '--font-size-xl': '18px',
    '--font-size-xxl': '20px',

    // Icon
    '--icon-size-xs': '6px',
    '--icon-size-xsm': '8px',
    '--icon-size-sm': '12px',
    '--icon-size-md': '14px',
    '--icon-size-lg': '16px',
    '--icon-size-xl': '18px',
    '--icon-size-xxl': '20px',
    '--icon-size-xxxl': '24px',
  },
  large: {
    '--font-size-xs': '12px',
    '--font-size-sm': '14px',
    '--font-size-md': '16px',
    '--font-size-lg': '18px',
    '--font-size-xl': '20px',
    '--font-size-xxl': '22px',

    // Icon
    '--icon-size-xs': '8px',
    '--icon-size-xsm': '10px',
    '--icon-size-sm': '14px',
    '--icon-size-md': '16px',
    '--icon-size-lg': '18px',
    '--icon-size-xl': '20px',
    '--icon-size-xxl': '22px',
    '--icon-size-xxxl': '26px',
  },
  xlarge: {
    '--font-size-xs': '14px',
    '--font-size-sm': '16px',
    '--font-size-md': '18px',
    '--font-size-lg': '20px',
    '--font-size-xl': '22px',
    '--font-size-xxl': '24px',

    // Icon
    '--icon-size-xs': '10px',
    '--icon-size-xsm': '12px',
    '--icon-size-sm': '16px',
    '--icon-size-md': '18px',
    '--icon-size-lg': '20px',
    '--icon-size-xl': '22px',
    '--icon-size-xxl': '24px',
    '--icon-size-xxxl': '28px',
  },
};

export function FontSizeProvider({ children }) {
  const [currentFontSize, setCurrentFontSize] = useState(
    () => localStorage.getItem('user-font-size-preset') || 'default',
  );

  const [fontSuggestion, setFontSuggestion] = useState(null);
  const [isUserOverrideFontSize, setIsUserOverrideFontSize] = useState(false);

  useEffect(() => {
    if (!currentFontSize) return;
    Object.entries(fontPresets[currentFontSize]).forEach(([key, value]) =>
      document.documentElement.style.setProperty(key, value),
    );
    localStorage.setItem('user-font-size-preset', currentFontSize);
  }, [currentFontSize]);

  useEffect(() => {
    const updateFontSize = () => {
      const screenWidth = window.innerWidth;

      let recommendedFontSize;

      if (screenWidth < 768) {
        recommendedFontSize = 'default';
      } else if (screenWidth >= 768 && screenWidth <= 1440) {
        recommendedFontSize = 'small';
      } else if (screenWidth < 2200) {
        recommendedFontSize = 'default';
      } else {
        recommendedFontSize = 'xlarge';
      }

      setFontSuggestion(recommendedFontSize);

      if (!isUserOverrideFontSize) {
        setCurrentFontSize(recommendedFontSize);
      }
    };

    updateFontSize();
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, [isUserOverrideFontSize]);

  const handleSetFontSize = (value) => {
    setIsUserOverrideFontSize(true);
    setCurrentFontSize(value);
  };

  const contextValue = useMemo(
    () => ({
      currentFontSize,
      setCurrentFontSize: handleSetFontSize,
      fontSuggestion,
    }),
    [currentFontSize, fontSuggestion],
  );

  return (
    <FontSizeContext.Provider value={contextValue}>
      {children}
    </FontSizeContext.Provider>
  );
}

export const useFontSize = () => useContext(FontSizeContext);
